import { UNLABELLED_ASSET_LABEL } from "@overmap-ai/common"
import type {
	Asset,
	AssetTypeIdentifier,
	AssetTypeStatus,
	ProcedureInitiative,
	ProcedureStep,
	ProcedureType,
	Stored,
} from "@overmap-ai/common"
import { ConditionManager, FieldSchema } from "@overmap-ai/forms"
import * as turf from "@turf/turf"

import { computeProcedureInitiativeIndex } from "../../components/IndexLabels/utils"
import type { ProcedureData, ProcedureInitiativeData } from "../../typings"
import { combineColumnDefs, CSVExporter } from "./CSVExporter"
import type { CSVColumnDef } from "./typings"
import { getCSVColumnDefsOfFieldSchema } from "./utils/fields"
import { getCSVColumnDefsOfIdentifiers } from "./utils/identifiers"

interface ProcedureDataCsvExporterConfig {
	procedureType: Stored<ProcedureType>
	assets: Stored<Asset>[]
	procedureSteps: Stored<ProcedureStep>[]
	procedureInitiatives: Stored<ProcedureInitiative>[]
	procedureTypeFieldSchema: FieldSchema
	procedureStepFieldSchemas: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]>
}

export const getProcedureDataCsvExporter = (data: ProcedureData[], config: ProcedureDataCsvExporterConfig) => {
	const {
		procedureType,
		assets,
		procedureSteps,
		procedureInitiatives,
		procedureTypeFieldSchema,
		procedureStepFieldSchemas,
	} = config

	const assetsById = Object.fromEntries(assets.map((asset) => [asset.uuid, asset]))
	const procedureInitiativesById = Object.fromEntries(
		procedureInitiatives.map((procedureInitiative) => [procedureInitiative.uuid, procedureInitiative]),
	)

	const baseColumns: CSVColumnDef<ProcedureData>[] = [
		{ id: "uuid", header: "uuid", accessorFn: (row) => row.procedure.uuid },
		{
			id: "asset",
			header: "Asset",
			accessorFn: (row) => {
				const asset = assetsById[row.procedure.asset]
				return asset.label || UNLABELLED_ASSET_LABEL
			},
		},
		{
			id: "procedure_initiative",
			header: "Initiative",
			accessorFn: (row) => {
				const procedureInitiativeId = row.procedure.procedure_initiative
				if (!procedureInitiativeId) return ""
				const procedureInitiative = procedureInitiativesById[procedureInitiativeId]
				return computeProcedureInitiativeIndex(procedureType, procedureInitiative)
			},
		},
	]

	const procedureTypeFieldColumns = getCSVColumnDefsOfFieldSchema<ProcedureData>(
		procedureTypeFieldSchema,
		(row) => row.procedureFieldValues?.values,
	)

	const procedureStepColumns = procedureSteps.flatMap((procedureStep) => {
		const [fieldSchema] = procedureStepFieldSchemas[procedureStep.uuid]

		const procedureStepFieldColumns = getCSVColumnDefsOfFieldSchema<ProcedureData>(
			fieldSchema,
			(row) => row.procedureStepFieldValues[procedureStep.uuid]?.values,
		)

		return combineColumnDefs(procedureStepFieldColumns)
	})

	return new CSVExporter({
		columns: combineColumnDefs(baseColumns, procedureTypeFieldColumns, procedureStepColumns),
		data: data,
	})
}

interface ProcedureInitiativeDataCsvExporterConfig {
	procedureSteps: Stored<ProcedureStep>[]
	procedureTypeFieldSchema: FieldSchema
	procedureStepFieldSchemas: Record<ProcedureStep["uuid"], [FieldSchema, ConditionManager]>
	assetTypeFieldSchema: FieldSchema
	assetTypeStatuses: Stored<AssetTypeStatus>[]
	assetTypeIdentifiers: Stored<AssetTypeIdentifier>[]
}

export const getProcedureInitiativeDataCsvExporter = (
	data: ProcedureInitiativeData[],
	config: ProcedureInitiativeDataCsvExporterConfig,
) => {
	const {
		procedureSteps,
		procedureTypeFieldSchema,
		procedureStepFieldSchemas,
		assetTypeFieldSchema,
		assetTypeStatuses,
		assetTypeIdentifiers,
	} = config

	const assetTypeStatusesById = Object.fromEntries(
		assetTypeStatuses.map((assetTypeStatus) => [assetTypeStatus.uuid, assetTypeStatus]),
	)

	const baseColumns: CSVColumnDef<ProcedureInitiativeData>[] = [
		{ id: "uuid", header: "uuid", accessorFn: (row) => row.procedure.uuid },
		{
			id: "asset",
			header: "Asset",
			accessorFn: (row) => {
				const asset = row.asset
				return asset.label || UNLABELLED_ASSET_LABEL
			},
		},
		{ id: "geometry", header: "Asset Geometry", accessorFn: (row) => JSON.stringify(row.asset.geometry) },
		{
			id: "lat_centroid",
			header: "Asset Lat centroid",
			accessorFn: (row) => {
				if (!row.asset.geometry) return ""
				const centroid = turf.centroid(row.asset.geometry)
				return centroid.geometry.coordinates[1].toString()
			},
		},
		{
			id: "long_centroid",
			header: "Asset Long centroid",
			accessorFn: (row) => {
				if (!row.asset.geometry) return ""
				const centroid = turf.centroid(row.asset.geometry)
				return centroid.geometry.coordinates[0].toString()
			},
		},
		{
			id: "status",
			header: "Asset Status",
			accessorFn: (row) => assetTypeStatusesById[row.asset.status]?.name ?? "",
		},
	]

	const assetTypeIdentifierColumns = getCSVColumnDefsOfIdentifiers(
		assetTypeIdentifiers,
		(identifier) => (row: ProcedureInitiativeData) => row.assetTypeIdentifierValues[identifier.uuid]?.value,
	)

	const assetTypeFieldColumns = getCSVColumnDefsOfFieldSchema<ProcedureInitiativeData>(
		assetTypeFieldSchema,
		(row) => row.assetTypeFieldValues?.values,
	)

	const procedureTypeFieldColumns = getCSVColumnDefsOfFieldSchema<ProcedureInitiativeData>(
		procedureTypeFieldSchema,
		(row) => row.procedureFieldValues?.values,
	)

	const procedureStepColumns = procedureSteps.flatMap((procedureStep) => {
		const [fieldSchema] = procedureStepFieldSchemas[procedureStep.uuid]

		const procedureStepFieldColumns = getCSVColumnDefsOfFieldSchema<ProcedureInitiativeData>(
			fieldSchema,
			(row) => row.procedureStepFieldValues[procedureStep.uuid]?.values,
		)

		return combineColumnDefs(procedureStepFieldColumns)
	})

	return new CSVExporter({
		columns: combineColumnDefs(
			baseColumns,
			assetTypeIdentifierColumns,
			assetTypeFieldColumns,
			procedureTypeFieldColumns,
			procedureStepColumns,
		),
		data,
	})
}
